// UUID list - matches mobile/src/screens/workout-monitor/constants.ts
// https://bitbucket.org/bluetooth-SIG/public/src/main/assigned_numbers/uuids/characteristic_uuids.yaml
export const FTMS_SERVICE_UUID = '00001826-0000-1000-8000-00805f9b34fb';
export const DEVICE_INFORMATION_SERVICE_UUID =
  '0000180a-0000-1000-8000-00805f9b34fb';
export const MANUFACTURER_NAME_CHARACTERISTIC_UUID =
  '00002a29-0000-1000-8000-00805f9b34fb';
export const FIRMWARE_REVISION_CHARACTERISTIC_UUID =
  '00002a26-0000-1000-8000-00805f9b34fb';
export const HARDWARE_REVISION_CHARACTERISTIC_UUID =
  '00002a27-0000-1000-8000-00805f9b34fb';

export const ROW_FTMS_CHARACTERISTIC_UUID =
  '00002ad1-0000-1000-8000-00805f9b34fb';
export const BIKE_FTMS_CHARACTERISTIC_UUID =
  '00002ad2-0000-1000-8000-00805f9b34fb';
export const RUN_FTMS_CHARACTERISTIC_UUID =
  '00002acd-0000-1000-8000-00805f9b34fb';

export const FTMS_CONTROL_CHARACTERISTIC_UUID =
  '00002ad9-0000-1000-8000-00805f9b34fb';

export type ErgType = 'row' | 'bike' | 'run';

export enum ControlPoint {
  RequestControl = 0x00,
  Reset = 0x01,
  StartSession = 0x07,
  TargetDistance = 0x0c,
  TargetTime = 0x0d,
}

export function getByte(value: number, byteIndex: number): number {
  return (value >> (byteIndex * 8)) & 255;
}

// Field descriptions and lengths:
// https://bitbucket.org/bluetooth-SIG/public/src/main/gss/org.bluetooth.characteristic.rower_data.yaml
export interface RowerData {
  'Stroke Rate'?: number;
  'Stroke Count'?: number;
  'Average Stroke Rate'?: number;
  'Total Distance'?: number;
  'Instantaneous Pace'?: number;
  'Average Pace'?: number;
  'Instantaneous Power'?: number;
  'Average Power'?: number;
  'Resistance Level'?: number;
  'Total Energy'?: number;
  'Energy Per Hour'?: number;
  'Energy Per Minute'?: number;
  'Heart Rate'?: number;
  'Metabolic Equivalent'?: number;
  'Elapsed Time'?: number;
  'Remaining Time'?: number;
}

function readUIntLE(
  value: DataView,
  byteIndex: number,
  length: number
): number {
  if (byteIndex + length - 1 < value.byteLength) {
    if (length === 1) return value.getUint8(byteIndex);
    if (length === 2) return value.getUint16(byteIndex, true);
    if (length === 3)
      return (
        value.getUint16(byteIndex, true) + (value.getUint8(byteIndex + 2) << 16)
      );
  }
  return 0;
}

function readIntLE(value: DataView, byteIndex: number, length: number): number {
  if (byteIndex + length - 1 < value.byteLength) {
    if (length === 2) return value.getInt16(byteIndex, true);
  }
  return 0;
}

export function parseRowerData(value: DataView): RowerData {
  const flags = readUIntLE(value, 0, 2);
  let byteIndex = 2;
  const data: RowerData = {};

  if ((flags & (1 << 0)) === 0) {
    data['Stroke Rate'] = readUIntLE(value, byteIndex, 1) / 2;
    byteIndex += 1;

    data['Stroke Count'] = readUIntLE(value, byteIndex, 2);
    byteIndex += 2;
  }

  if ((flags & (1 << 1)) !== 0) {
    data['Average Stroke Rate'] = readUIntLE(value, byteIndex, 1) / 2;
    byteIndex += 1;
  }

  if ((flags & (1 << 2)) !== 0) {
    let totalDistance = readUIntLE(value, byteIndex, 3);
    data['Total Distance'] = totalDistance;
    byteIndex += 3;
  }

  if ((flags & (1 << 3)) !== 0) {
    const currentPace = readUIntLE(value, byteIndex, 2);
    // water rower sends 65535 when the handle is not moving
    if (currentPace === 65535) {
      data['Instantaneous Pace'] = 0;
    } else {
      data['Instantaneous Pace'] = currentPace;
    }
    byteIndex += 2;
  }

  if ((flags & (1 << 4)) !== 0) {
    const avgPace = readUIntLE(value, byteIndex, 2);
    data['Average Pace'] = avgPace;
    byteIndex += 2;
  }

  if ((flags & (1 << 5)) !== 0) {
    const currentPower = readIntLE(value, byteIndex, 2);
    data['Instantaneous Power'] = currentPower;
    byteIndex += 2;
  }

  if ((flags & (1 << 6)) !== 0) {
    const avgPower = readIntLE(value, byteIndex, 2);
    data['Average Power'] = avgPower;
    byteIndex += 2;
  }

  if ((flags & (1 << 7)) !== 0) {
    data['Resistance Level'] = readUIntLE(value, byteIndex, 1);
    byteIndex += 1;
  }

  if ((flags & (1 << 8)) !== 0) {
    data['Total Energy'] = readUIntLE(value, byteIndex, 2);
    byteIndex += 2;

    data['Energy Per Hour'] = readUIntLE(value, byteIndex, 2);
    byteIndex += 2;

    data['Energy Per Minute'] = readUIntLE(value, byteIndex, 1);
    byteIndex += 1;
  }

  if ((flags & (1 << 9)) !== 0) {
    data['Heart Rate'] = readUIntLE(value, byteIndex, 1);
    byteIndex += 1;
  }

  if ((flags & (1 << 10)) !== 0) {
    data['Metabolic Equivalent'] = readUIntLE(value, byteIndex, 1);
    byteIndex += 1;
  }

  if ((flags & (1 << 11)) !== 0) {
    data['Elapsed Time'] = readUIntLE(value, byteIndex, 2);
    byteIndex += 2;
  }

  if ((flags & (1 << 12)) !== 0) {
    data['Remaining Time'] = readUIntLE(value, byteIndex, 2);
    byteIndex += 2;
  }

  return data;
}

// Bike data - matches mobile FTMSDataCharacteristic BikeDataCharacteristic
export interface BikeData {
  'Current Speed (km/h)'?: number;
  'Average Speed (km/h)'?: number;
  'Stroke/Cadence Rate'?: number;
  'Average Cadence'?: number;
  'Total Distance'?: number;
  'Resistance Level'?: number;
  'Instantaneous Power'?: number;
  'Average Power'?: number;
  'Total Energy'?: number;
  'Energy Per Hour'?: number;
  'Energy Per Minute'?: number;
  'Heart Rate'?: number;
  'Metabolic Equivalent'?: number;
  'Elapsed Time'?: number;
  'Remaining Time'?: number;
}

export function parseBikeData(value: DataView): BikeData {
  const flags = readUIntLE(value, 0, 2);
  let byteIndex = 2;
  const data: BikeData = {};

  if ((flags & (1 << 0)) === 0) {
    data['Current Speed (km/h)'] = readUIntLE(value, byteIndex, 2) * 0.01;
    byteIndex += 2;
  }
  if ((flags & (1 << 1)) !== 0) {
    data['Average Speed (km/h)'] = readUIntLE(value, byteIndex, 2) * 0.01;
    byteIndex += 2;
  }
  if ((flags & (1 << 2)) !== 0) {
    data['Stroke/Cadence Rate'] = readUIntLE(value, byteIndex, 2) / 2;
    byteIndex += 2;
  }
  if ((flags & (1 << 3)) !== 0) {
    data['Average Cadence'] = readUIntLE(value, byteIndex, 2) / 2;
    byteIndex += 2;
  }
  if ((flags & (1 << 4)) !== 0) {
    data['Total Distance'] = readUIntLE(value, byteIndex, 3);
    byteIndex += 3;
  }
  if ((flags & (1 << 5)) !== 0) {
    data['Resistance Level'] = readUIntLE(value, byteIndex, 2);
    byteIndex += 2;
  }
  if ((flags & (1 << 6)) !== 0) {
    data['Instantaneous Power'] = readIntLE(value, byteIndex, 2);
    byteIndex += 2;
  }
  if ((flags & (1 << 7)) !== 0) {
    data['Average Power'] = readIntLE(value, byteIndex, 2);
    byteIndex += 2;
  }
  if ((flags & (1 << 8)) !== 0) {
    data['Total Energy'] = readUIntLE(value, byteIndex, 2);
    byteIndex += 2;
    data['Energy Per Hour'] = readUIntLE(value, byteIndex, 2);
    byteIndex += 2;
    data['Energy Per Minute'] = readUIntLE(value, byteIndex, 1);
    byteIndex += 1;
  }
  if ((flags & (1 << 9)) !== 0) {
    data['Heart Rate'] = readUIntLE(value, byteIndex, 1);
    byteIndex += 1;
  }
  if ((flags & (1 << 10)) !== 0) {
    data['Metabolic Equivalent'] = readUIntLE(value, byteIndex, 1);
    byteIndex += 1;
  }
  if ((flags & (1 << 11)) !== 0) {
    data['Elapsed Time'] = readUIntLE(value, byteIndex, 2);
    byteIndex += 2;
  }
  if ((flags & (1 << 12)) !== 0) {
    data['Remaining Time'] = readUIntLE(value, byteIndex, 2);
    byteIndex += 2;
  }
  return data;
}

// Run data - matches mobile FTMSDataCharacteristic RunDataCharacteristic
export interface RunData {
  'Current Speed (km/h)'?: number;
  'Average Speed (km/h)'?: number;
  'Total Distance'?: number;
  'Instantaneous Pace'?: number;
  'Average Pace'?: number;
  'Total Energy'?: number;
  'Energy Per Hour'?: number;
  'Energy Per Minute'?: number;
  'Heart Rate'?: number;
  'Metabolic Equivalent'?: number;
  'Elapsed Time'?: number;
  'Remaining Time'?: number;
}

export function parseRunData(value: DataView): RunData {
  const flags = readUIntLE(value, 0, 2);
  let byteIndex = 2;
  const data: RunData = {};

  if ((flags & (1 << 0)) === 0) {
    data['Current Speed (km/h)'] = readUIntLE(value, byteIndex, 2) * 0.01;
    byteIndex += 2;
  }
  if ((flags & (1 << 1)) !== 0) {
    data['Average Speed (km/h)'] = readUIntLE(value, byteIndex, 2) * 0.01;
    byteIndex += 2;
  }
  if ((flags & (1 << 2)) !== 0) {
    data['Total Distance'] = readUIntLE(value, byteIndex, 3);
    byteIndex += 3;
  }
  if ((flags & (1 << 3)) !== 0) {
    byteIndex += 4; // Inclination and Ramp Angle
  }
  if ((flags & (1 << 4)) !== 0) {
    byteIndex += 4; // Elevation Gain
  }
  if ((flags & (1 << 5)) !== 0) {
    data['Instantaneous Pace'] = readUIntLE(value, byteIndex, 1);
    byteIndex += 1;
  }
  if ((flags & (1 << 6)) !== 0) {
    data['Average Pace'] = readUIntLE(value, byteIndex, 1);
    byteIndex += 1;
  }
  if ((flags & (1 << 7)) !== 0) {
    data['Total Energy'] = readUIntLE(value, byteIndex, 2);
    byteIndex += 2;
    data['Energy Per Hour'] = readUIntLE(value, byteIndex, 2);
    byteIndex += 2;
    data['Energy Per Minute'] = readUIntLE(value, byteIndex, 1);
    byteIndex += 1;
  }
  if ((flags & (1 << 8)) !== 0) {
    data['Heart Rate'] = readUIntLE(value, byteIndex, 1);
    byteIndex += 1;
  }
  if ((flags & (1 << 9)) !== 0) {
    data['Metabolic Equivalent'] = readUIntLE(value, byteIndex, 1);
    byteIndex += 1;
  }
  if ((flags & (1 << 10)) !== 0) {
    data['Elapsed Time'] = readUIntLE(value, byteIndex, 2);
    byteIndex += 2;
  }
  if ((flags & (1 << 11)) !== 0) {
    data['Remaining Time'] = readUIntLE(value, byteIndex, 2);
    byteIndex += 2;
  }
  return data;
}

export function parseFtmsData(
  value: DataView,
  ergType: ErgType
): RowerData | BikeData | RunData {
  if (ergType === 'bike') return parseBikeData(value);
  if (ergType === 'run') return parseRunData(value);
  return parseRowerData(value);
}

export function rawBytesToHex(value: DataView): string[] {
  const a: string[] = [];
  for (let i = 0; i < value.byteLength; i++) {
    a.push('0x' + ('00' + value.getUint8(i).toString(16)).slice(-2));
  }
  return a;
}

export function rawBytesString(dataView: DataView): string {
  // Create a Uint8Array view of the buffer for easier processing
  const uint8Array = new Uint8Array(
    dataView.buffer,
    dataView.byteOffset,
    dataView.byteLength
  );
  // Convert the Uint8Array to a binary string
  let binaryString = '';
  for (let i = 0; i < uint8Array.length; i++) {
    binaryString += String.fromCharCode(uint8Array[i]);
  }
  // Encode the binary string to Base64
  return btoa(binaryString);
}
